import React from 'react';
import { X } from 'lucide-react';
import type { LotFilter } from '../../types/lot';

interface LotFilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  filters: LotFilter;
  onFilterChange: (filters: Partial<LotFilter>) => void;
  communities: string[];
  zones: string[];
  leaseIds: string[];
  leaseholders: string[];
  zoneDescriptions?: Record<string, string>;
}

export default function LotFilterPanel({
  isOpen,
  onClose,
  filters,
  onFilterChange,
  communities,
  zones,
  leaseIds,
  leaseholders,
  zoneDescriptions = {}
}: LotFilterPanelProps) {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />
      {/* Panel */}
      <div className="fixed inset-x-0 bottom-0 sm:inset-auto sm:absolute sm:right-0 sm:top-full sm:mt-2 z-50">
        <div className="w-full sm:w-96 bg-white rounded-t-lg sm:rounded-lg shadow-lg border border-gray-200">
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Filters</h3>
              <button 
                onClick={onClose} 
                className="p-2 -mr-2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Community
                </label>
                <select
                  value={filters.community || ''}
                  onChange={(e) => onFilterChange({ community: e.target.value || null })}
                  className="w-full h-12 sm:h-auto rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">All Communities</option>
                  {communities.map((community) => (
                    <option key={community} value={community}>
                      {community}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lease ID
                </label>
                <select
                  value={filters.leaseId || ''}
                  onChange={(e) => onFilterChange({ leaseId: e.target.value || null })}
                  className="w-full h-12 sm:h-auto rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">All Lease IDs</option>
                  {leaseIds.map((leaseId) => (
                    <option key={leaseId} value={leaseId}>
                      {leaseId}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Zone
                </label>
                <select
                  value={filters.zone || ''}
                  onChange={(e) => onFilterChange({ zone: e.target.value || null })}
                  className="w-full h-12 sm:h-auto rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">All Zones</option>
                  {zones.map((zone) => (
                    <option key={zone} value={zone}>
                      {zoneDescriptions[zone] || zone}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Leaseholder
                </label>
                <select
                  value={filters.leaseholder || ''}
                  onChange={(e) => onFilterChange({ leaseholder: e.target.value || null })}
                  className="w-full h-12 sm:h-auto rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">All Leaseholders</option>
                  {leaseholders.map((leaseholder) => (
                    <option key={leaseholder} value={leaseholder}>
                      {leaseholder}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lot Available
                </label>
                <select
                  value={filters.lotAvail || ''}
                  onChange={(e) => onFilterChange({ lotAvail: e.target.value as 'Y' | 'N' | null })}
                  className="w-full h-12 sm:h-auto rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">All</option>
                  <option value="Y">Yes</option>
                  <option value="N">No</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lot Surrendered
                </label>
                <select
                  value={filters.lotSurr || ''}
                  onChange={(e) => onFilterChange({ lotSurr: e.target.value as 'Y' | 'N' | null })}
                  className="w-full h-12 sm:h-auto rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">All</option>
                  <option value="Y">Yes</option>
                  <option value="N">No</option>
                </select>
              </div>

              <div className="pt-4 border-t">
                <button
                  onClick={() => onFilterChange({
                    community: null,
                    leaseId: null,
                    zone: null,
                    leaseholder: null,
                    lotAvail: null, // Reset new filter
                    lotSurr: null   // Reset new filter
                  })}
                  className="w-full h-12 sm:h-auto px-4 py-2 text-sm text-primary hover:bg-primary/5 rounded-md"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
